import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  hydrateLearningInviteProgress,
  resetLearningInviteProgress,
  setLearningRecipientEmail,
  setLearningPackageError,
  setLearningPackageLoading,
  setLearningPackageSuccess,
} from '../../redux/learning/actions';
import { fetchLearningInvitePackage } from '../helpers/fetchLearningInvitePackage';
import { fetchLearningPackage } from '../helpers/fetchLearningPackage';
import { mapLearningInviteProgress, mapLearningInviteRecipientEmail } from '../helpers/mapLearningInviteProgress';
import {
  selectLearningPackageData,
  selectLearningPackageError,
  selectLearningPackageId,
  selectLearningPackageLoading,
} from '../../redux/learning/selectors';
import { useLearningQuery } from './useLearningQuery';

/** Loads package config and invite progress on mount / URL param change. */
export function useLearningPackage() {
  const dispatch = useDispatch();
  const { guidanceVersion, inviteId, userId } = useLearningQuery();
  const packageId = useSelector(selectLearningPackageId);
  const packageData = useSelector(selectLearningPackageData);
  const loading = useSelector(selectLearningPackageLoading);
  const error = useSelector(selectLearningPackageError);

  useEffect(() => {
    let cancelled = false;

    const loadPackage = async () => {
      if (!packageId) {
        dispatch(setLearningPackageError('ld-package-not-configured'));
        return;
      }

      dispatch(setLearningPackageLoading(true));

      try {
        const packagePromise = fetchLearningPackage(packageId, {
          version: guidanceVersion,
        });
        const invitePromise = inviteId
          ? fetchLearningInvitePackage({
            inviteId,
            packageId,
            userId,
            guidanceVersion,
          }).catch(() => null)
          : Promise.resolve(null);

        const [data, inviteData] = await Promise.all([packagePromise, invitePromise]);

        if (cancelled) {
          return;
        }

        dispatch(setLearningPackageSuccess(data));
        dispatch(setLearningRecipientEmail(mapLearningInviteRecipientEmail(inviteData)));

        const inviteProgress = mapLearningInviteProgress(inviteData);
        if (inviteProgress) {
          dispatch(hydrateLearningInviteProgress(inviteProgress));
        } else {
          dispatch(resetLearningInviteProgress());
        }
      } catch (loadError) {
        if (!cancelled) {
          dispatch(
            setLearningPackageError(
              loadError?.message || 'ld-unable-load-module',
            ),
          );
        }
      }
    };

    loadPackage();

    return () => {
      cancelled = true;
    };
  }, [dispatch, guidanceVersion, inviteId, packageId, userId]);

  return { packageId, packageData, loading, error };
}
